package com.downdogapp.client.views.playback

import com.downdogapp.*
import com.downdogapp.AlignItems.CENTER
import com.downdogapp.Color.Companion.NEARLY_OPAQUE_GREY
import com.downdogapp.Display.NONE
import com.downdogapp.JustifyContent.SPACE_BETWEEN
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.client.*
import com.downdogapp.client.controllers.playback.PlaybackUtil
import com.downdogapp.client.controllers.playback.PlaybackViewController
import com.downdogapp.client.widget.*
import kotlinx.browser.window
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLSpanElement

class SettingsMenuView(private var anchorElement: HTMLElement,
                       private val controller: PlaybackViewController) : AbstractView(), View {

  var visible = false; private set

  private val settingToggles: List<Toggle>

  override val root = createDiv {
    position = ABSOLUTE
    display = NONE

    width = OUTER_CONTAINER_WIDTH
    bgColor = NEARLY_OPAQUE_GREY
    borderRadius = px(8)

    verticalFlexBox(horizontalAlign = CENTER) {
      margin = px(20)

      if (controller.supportsChangeVideoQuality) {
        horizontalFlexBox(horizontalJustify = SPACE_BETWEEN, verticalAlign = CENTER) {
          position = RELATIVE
          width = percent(100)
          height = px(40)

          settingsTitle(Strings.VIDEO_QUALITY)

          val videoQualityButton = textButton(
            text = MANIFEST.videoQualities.find { it.id == PlaybackUtil.videoQualityId }!!.label,
            fontSize = 14,
            fontWeight = FontWeight.REGULAR,
            clickFn = null
          )
          videoQualityButton.onClick {
            controller.settingsVideoQualityButtonClicked {
              videoQualityButton.textContent =
                MANIFEST.videoQualities.find { it.id == PlaybackUtil.videoQualityId }!!.label
            }
          }
        }
      }

      for (playlistSettingTypeId in MANIFEST.playlistSettingTypeIds) {
        if (controller.supportsChangePlaylistSetting(playlistSettingTypeId)) {
          horizontalFlexBox(horizontalJustify = SPACE_BETWEEN, verticalAlign = CENTER) {
            position = RELATIVE
            width = percent(100)
            height = px(40)

            settingsTitle(SequenceSettings.getTypeLabel(playlistSettingTypeId) ?: Strings.MUSIC)

            val playlistTypeButton = textButton(
              text = SequenceSettings.getSelectedLabel(playlistSettingTypeId)!!,
              fontSize = 14,
              fontWeight = FontWeight.REGULAR,
              clickFn = null
            )
            playlistTypeButton.onClick {
              controller.settingsPlaylistButtonClicked(playlistSettingTypeId) {
                playlistTypeButton.textContent = SequenceSettings.getSelectedLabel(playlistSettingTypeId)!!
              }
            }
          }
        }
      }

      settingToggles = listOfNotNull(
        if (controller.supportsPoseNames)
          toggleSettingRow(
            label = controller.showPoseNamesLabel,
            getValue = { PlaybackUtil.displayPoseNames },
            onValueChanged = { controller.setShowPoseNames(it) }
          )
        else
          null,
        if (controller.supportsLikeAndExcludeButtons)
          toggleSettingRow(
            label = Strings.SHOW_LIKE_AND_EXCLUDE_BUTTONS,
            getValue = { PlaybackUtil.showLikeAndExcludeButtons },
            onValueChanged = { controller.setShowLikeAndExcludeButtons(it) }
          )
        else
          null,
        if (controller.supportsCountdown)
          toggleSettingRow(
            label = Strings.COUNTDOWN_TIMER,
            getValue = { PlaybackUtil.showCountdown },
            onValueChanged = { controller.setShowCountdown(it) }
          )
        else
          null,
        if (controller.supportsOverlay)
          toggleSettingRow(
            label = Strings.SHOW_OVERLAY,
            getValue = { PlaybackUtil.showOverlay },
            onValueChanged = { controller.setShowOverlay(it) }
          )
        else
          null,
        toggleSettingRow(
          label = Strings.KEEP_TIMELINE_VISIBLE,
          getValue = { PlaybackUtil.keepTimelineVisible },
          onValueChanged = { controller.setKeepTimelineVisible(it) }
        ),
        if (controller.supportsPosesOnTimeline)
          toggleSettingRow(
            label = controller.showPosesOnTimelineLabel,
            getValue = { PlaybackUtil.showPosesOnTimeline },
            onValueChanged = { controller.setPreferPosesOnTimeline(it) }
          )
        else
          null
      )

      roundedTextButton(
        text = Strings.SEND_FEEDBACK,
        fontSize = 12,
        clickFn = controller::onSendFeedbackClicked
      ) {
        marginTop = px(15)
      }
    }
  }

  fun onViewLoaded() {
    settingToggles.forEach {
      it.refresh()
    }
  }

  fun reposition() {
    // Calculate distance of anchor's TOP RIGHT from window's BOTTOM RIGHT
    val boundingBox = anchorElement.getBoundingClientRect()
    val x = window.innerWidth - boundingBox.x - boundingBox.width
    val y = window.innerHeight - boundingBox.y

    root.apply {
      right = px(x)
      bottom = px(y) + VERTICAL_DISTANCE_FROM_ICON
    }
  }

  override fun fadeIn(duration: Duration, onComplete: View.() -> Unit) {
    super.fadeIn(duration, onComplete)
    visible = true
    reposition()
  }

  override fun fadeOut(duration: Duration, onComplete: View.() -> Unit) {
    super.fadeOut(duration, onComplete)
    visible = false
  }

  override fun show() {
    super.show()
    visible = true
    reposition()
  }

  override fun hide() {
    super.hide()
    visible = false
  }

  companion object {

    val OUTER_CONTAINER_WIDTH = px(360)
    val VERTICAL_DISTANCE_FROM_ICON = px(30)

    private fun HTMLElement.settingsTitle(title: String,
                                          block: (HTMLSpanElement.() -> Unit) = {}) =
      label(title.uppercase(), 14, FontWeight.SEMIBOLD, block = block)

    private fun HTMLElement.toggleSettingRow(label: String,
                                             getValue: () -> Boolean,
                                             onValueChanged: (Boolean) -> Unit): Toggle {
      val toggle: Toggle
      horizontalFlexBox(horizontalJustify = SPACE_BETWEEN, verticalAlign = CENTER) {
        position = RELATIVE
        width = percent(100)
        height = px(40)

        settingsTitle(label) {
          paddingRight = px(10)
        }
        toggle = toggle(getValue, onValueChanged)
      }
      return toggle
    }
  }
}

fun HTMLElement.settingsMenuView(anchorElement: HTMLElement,
                                 controller: PlaybackViewController,
                                 init: HTMLDivElement.() -> Unit = {}): SettingsMenuView {
  val view = SettingsMenuView(anchorElement, controller)
  append(view.root)
  view.root.apply(init)
  return view
}
